/**
 * authentikClient — der OIDC-Kern für den Authentik-Modus (VITE_AUTH_MODE=authentik).
 *
 * Enthält alles, was in jeder Anwendung gleich ist: UserManager, base-bewusste
 * Rücksprung-Adressen, Rollen aus den Claims, Abmelden. Die Verdrahtung mit dem
 * jeweiligen Auth-Store steht in AuthentikAuth.tsx — die unterscheidet sich von
 * App zu App, dieser Kern nicht.
 *
 * Konfiguration (Build-Zeit, siehe frontend/.env.example + Dockerfile-ARGs):
 *  - VITE_OIDC_ISSUER      https://auth.rmnc.app/application/o/<slug>/
 *  - VITE_OIDC_CLIENT_ID   Client-ID der Authentik-Application
 *
 * ABMELDEN (am Tester verifiziert, Vorlage rmnc-portal/src/auth/AuthentikAuth.tsx):
 * NUR die Kombination wirkt — lokalen Token-Store leeren UND die zentrale
 * Sitzung über /flows/-/default/invalidation/ beenden. signoutRedirect()
 * scheitert am strict-Matching des Providers, /if/flow/<slug>/ zeigt nur die
 * Oberfläche. Während des Abmeldens muss der Auto-Login-Redirect unterdrückt
 * werden (istAbmeldung), sonst bricht der neue Authorize-Flow die Invalidation
 * ab und die Sitzung lebt weiter ("sofort wieder drin").
 */

import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts"

const OIDC_ISSUER = (import.meta.env.VITE_OIDC_ISSUER as string | undefined) ?? ""
const OIDC_CLIENT_ID = (import.meta.env.VITE_OIDC_CLIENT_ID as string | undefined) ?? ""

/** Namen der fehlenden Build-Variablen — leer, wenn alles gesetzt ist. */
export function fehlendeKonfiguration(): string[] {
  return [
    !OIDC_ISSUER && "VITE_OIDC_ISSUER",
    !OIDC_CLIENT_ID && "VITE_OIDC_CLIENT_ID",
  ].filter(Boolean) as string[]
}

/**
 * Rücksprung nach der Anmeldung. Base-bewusst: unter /energie/ lautet er
 * /energie/callback — sonst landete er beim Portal, das unter demselben
 * Ursprung liegt (Ingress-Pfad-Muster, rmnc-plattform#6).
 */
export function callbackUri(): string {
  return `${window.location.origin}${import.meta.env.BASE_URL}callback`
}

/** Startseite dieser Anwendung, ebenfalls base-bewusst. */
export function startUri(): string {
  return `${window.location.origin}${import.meta.env.BASE_URL}`
}

let _userManager: UserManager | null = null
let _abmeldungLaeuft = false

export function userManager(): UserManager {
  if (!_userManager) {
    _userManager = new UserManager({
      authority: OIDC_ISSUER,
      client_id: OIDC_CLIENT_ID,
      redirect_uri: callbackUri(),
      post_logout_redirect_uri: startUri(),
      // "roles" ist NICHT optional: In Authentik läuft ein Property Mapping
      // nur, wenn sein Scope auch angefordert wird. Ohne diesen Scope kommt
      // kein roles-Claim an, alle Rollen-Gates greifen ins Leere — und zwar
      // lautlos, ohne Fehlermeldung.
      scope: "openid profile email roles",
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      automaticSilentRenew: true,
    })
  }
  return _userManager
}

export function istAbmeldung(): boolean {
  return _abmeldungLaeuft
}

/** Lokale Sitzung löschen und die zentrale Authentik-Sitzung beenden. */
export function abmelden(): void {
  _abmeldungLaeuft = true
  void userManager()
    .removeUser()
    .catch(() => undefined)
    .then(() => {
      const idpUrsprung = new URL(OIDC_ISSUER).origin
      window.location.href = `${idpUrsprung}/flows/-/default/invalidation/`
    })
}

/**
 * Rollen aus den Claims. Das Property Mapping liefert sie als `roles` und
 * zusätzlich als `groups`; die Gates erwarten dieselben Namen wie unter Logto
 * (admin, app:<teil>:<rolle>, …).
 */
export function rollenAusClaims(profile: Record<string, unknown>): string[] {
  const quelle = profile.roles ?? profile.groups
  return Array.isArray(quelle) ? quelle.map(String) : []
}

/** Access-Token der laufenden Sitzung, oder null. */
export async function accessToken(): Promise<string | null> {
  const u = await userManager().getUser()
  return u && !u.expired ? u.access_token : null
}

export type { User }
